'use strict'

import React, {Fragment} from 'react'
import ReactDOM from 'react-dom';
import MathQuilElement from './model-functions/MathQuilElement.jsx'

/**
 * Class to analyze a set of plot measures, to determine the structure of one table in Summary View.
 * This is a utility class, not a React component.
 * Shared with table-building functions (_constructOneTable(), etc).
 */
class MeasureSetAnalysis {
    constructor( plotMeasures, tableIndex, measureSet ) {
        // cross-references
        this.tableIndex = tableIndex;
        this.measureSet = measureSet;

        // new data needed to build HTML table
        this.colTypes = [];
        this.rowTypes = [];
        this.titleTypes = [];

        // analysis
        this.counts = {}; // numeric counts
        this.has = {}; // booleans
        let thisSet = this.measureSet;
        this.temp = {
            // local, private calculations
            // TODO: better to include this as part of analysis object
            //      numXAttributeIDs => analysis.count.xAttributeIDs {number}
            //      haveMeasures => analysis.has.measureNames {boolean}
            //
            numXAttributeIDs: plotMeasures.xAttributeIDs.length,
            numYAttributeIDs: plotMeasures.yAttributeIDs.length,
            numCAttributeIDs: plotMeasures.cAttributeIDs.length,
            numXBinNames: plotMeasures.xBinNames.length,
            numYBinNames: plotMeasures.yBinNames.length,
            numCBinNames: plotMeasures.cBinNames.length,
            numMeasureNames: thisSet.measureNames.length,
            //
            //haveCBinHeading: plotMeasures.cBinNamesHeading.length > 0,
            haveMeasures: thisSet.counts.value > 0,
            haveMeasuresRequiringAttributeID: thisSet.counts.attributeID > 0,
            haveColumnHeadingForRowType: false
        };

        this._analyzeAndDetermineRowsColumnsTitle( plotMeasures );
        this._analyzeAndBuildRowColumnTitleTypes( plotMeasures );
        this._analyzeRowsColumns( plotMeasures );
    }

    /**
     * Analysis step 1 of 3...determine what show go into rows, or columns, or title
     * @param plotMeasures {Dyn.PlotMeasures}
     * @private
     */
    _analyzeAndDetermineRowsColumnsTitle( plotMeasures ) {
        "use strict";
        let temp = this.temp,
            thisSet = this.measureSet;

        function showAttributeName( haveAttributes, haveAttributeCategories ) {
            return haveAttributes && // must have attribute on axis
                (!haveAttributeCategories) && // don't show the attribute name if we can show the attribute categories instead
                ( temp.haveMeasuresRequiringAttributeID || // and must have measures needing an attribute ID (e.g. mean, median but not count, percent)
                    !temp.haveMeasures ); // or if no measures at all show the attribute name
        }

        // determine what goes in the columns
        // NOTE: it would be better to remove these custom parameters, and have a more general mechanism for this,
        //      for example an explicit list of which measure parameter goes in each column, for each table type.
        //      similarly which parameters determine the rows, for each table type.
        temp.hasBodyColumnsForXBins       = (temp.numXBinNames > 0) && (thisSet.counts.xBinIndex > 0); // show X-axis categories only for tables that use them;
        temp.hasBodyColumnsForXAttributes = showAttributeName( (temp.numXAttributeIDs > 0) && (thisSet.setID !== "leastSq"), temp.hasBodyColumnsForXBins ); // && ! analysis.isLeastSquaresTable;
        temp.hasBodyColumnsForMeasures    = (temp.numMeasureNames > 0) && (
            (plotMeasures.setIDsWithMeasuresInColumns.includes(thisSet.setID)) || // these setIDs form a table with a column for each unique measure type
            (( temp.numXAttributeIDs === 0 ) && ( temp.numYAttributeIDs > 0 || temp.numCBinNames > 0 )) // else measures are on rows or title
        );
        temp.hasBodyColumnsForCBins       = false;

        // determine what goes in the rows
        temp.hasBodyRowsForXBins       = false;
        temp.hasBodyRowsForYBins       = (temp.numYBinNames > 0) && (thisSet.counts.yBinIndex > 0); // show Y-axis categories only for tables that use them
        temp.hasBodyRowsForXAttributes = showAttributeName( (temp.numXAttributeIDs > 1) && (thisSet.setID === "leastSq"), temp.hasBodyRowsForYBins );
        temp.hasBodyRowsForYAttributes = showAttributeName( (temp.numYAttributeIDs > ((thisSet.setID === "leastSq") ? 1 : 0 )), temp.hasBodyRowsForYBins );
        temp.hasBodyRowsForCBins       = (temp.numCBinNames > 0 ) && (thisSet.counts.cBinIndex > 0); // show color categories only for tables that use them
        //! (analysis.needTwoTables && ! analysis.isLeastSquaresTable); // show color categories for least squares table only, not with accompanying table showing mean, etc. to avoid many blank cells
        temp.hasBodyRowsForDivBins   = (thisSet.counts.divBinIndex > 0) && (plotMeasures.divBinNames.length > 0); // show divider names only for tables that use them
        temp.hasBodyRowsForHistoBins = (thisSet.counts.histoBinIndex > 0) && (plotMeasures.histoBinNames.length > 0); // show histogram bin names only for tables that use them
        temp.hasBodyRowsForLineID = Boolean(thisSet.paramSets.lineID); // use an untitled row for each unique line ID (for movable/reference lines which have no visible ID)
        //temp.hasHiddenLineIDs = Boolean(thisSet.hiddenID); // for Least Squares line which have a line ID not visible in table.
        if ( temp.hasBodyColumnsForXAttributes && temp.hasBodyRowsForYAttributes ) {
            // exception for SCATTERPLOT type situation: put X and Y attribute both on columns
            temp.hasBodyRowsForYAttributes               = false; // turn off, Y attributes go on X instead
            temp.appendYAttributesToXAttributeColumnList = true;
        }
        temp.hasBodyRowsForMeasures = ( temp.numMeasureNames > 0 && ! temp.hasBodyColumnsForMeasures ); // measures are on rows unless we put them on columns or title
        temp.hasBodyRowsForModelNumber = Boolean(thisSet.paramSets.modelNumber); // for setID:"modelFunction"

        // determine what goes in the table title
        temp.hasMeasuresInTitle     = (
            temp.numMeasureNames === 1 &&
            (temp.hasBodyRowsForYAttributes || temp.hasBodyRowsForYBins || temp.hasBodyRowsForCBins ||
                temp.hasBodyRowsForDivBins || temp.hasBodyRowsForHistoBins ) &&
            !(temp.hasBodyColumnsForMeasures));
        if( temp.hasMeasuresInTitle ) {
            temp.hasBodyRowsForMeasures = false; // EXCEPTION: display in title not in rows
        }
    }

    /**
     * Analysis step 2 of 3...organize items into row and column headings in the proper order
     * Populates the colTypes, rowTypes, titleTypes parameters
     * @param plotMeasures {Dyn.PlotMeasures}
     * @private
     */
    _analyzeAndBuildRowColumnTitleTypes( plotMeasures ) {
        let thisSet = this.measureSet,
            temp = this.temp,
            analysis = this;

        // build the list of column types (analysis.colTypes), which determines the column headings
        if( temp.hasBodyColumnsForXAttributes ) {
            let oneType = { items:plotMeasures.xAttributeIDs, selectorName:"attributeID", selectorType:"attributeID" };
            if (temp.appendYAttributesToXAttributeColumnList) {
                // list Y attributes after X attributes in columns, removing duplicates
                plotMeasures.yAttributeIDs.forEach( function( yAttributeID ) {
                    if( oneType.items.indexOf( yAttributeID ) === -1 ) {
                        oneType.items.push( yAttributeID );
                    }
                });
                //oneType.items = oneType.items.concat( plotMeasures.yAttributeIDs );
            }
            analysis.colTypes.push( oneType );
        }
        if( temp.hasBodyColumnsForXBins ) {
            analysis.colTypes.push({ items:plotMeasures.xBinNames, selectorName:"xBinIndex", selectorType:"itemIndex",
                isSelectable:true, isCategory:true, attributeID:plotMeasures.xAttributeIDs[0] });
        }
        if( temp.hasBodyColumnsForMeasures ) {
            analysis.colTypes.push({ items:thisSet.measureNames, selectorName:"measureName", selectorType:"itemName" });
        }
        if( analysis.colTypes.length === 0 ) { // add blank header column if none, may not be visible
            analysis.colTypes.push({ items:[""], selectorType:"none" }); // no selector
        }

        // build the list of row types (analysis.rowTypes), which determine the row headings
        if( temp.hasBodyRowsForXAttributes ) {
            analysis.rowTypes.push({ items:plotMeasures.xAttributeIDs, selectorName:"attributeID", selectorType:"attributeID" });
        }
        if( temp.hasBodyRowsForYAttributes ) {
            analysis.rowTypes.push({ items:plotMeasures.yAttributeIDs, selectorName:"attributeID", selectorType:"attributeID" });
        }
        if( temp.hasBodyRowsForYBins ) {
            analysis.rowTypes.push({ items:plotMeasures.yBinNames, selectorName:"yBinIndex", selectorType:"itemIndex",
                isSelectable:true, isCategory:true, attributeID:plotMeasures.yAttributeIDs[0] });
        }
        if( temp.hasBodyRowsForCBins ) {
            let oneType = { items:plotMeasures.cBinNames, selectorName:"cBinIndex", selectorType:"itemIndex", headingName:
                plotMeasures.cBinNamesHeading, isSelectable:true, isCategory:true, attributeID:plotMeasures.cAttributeIDs[0] };
            temp.haveColumnHeadingForRowType = Boolean( oneType.headingName.length );
            if( thisSet.counts.cBinIndex < thisSet.counts.value ) {
                oneType.items = oneType.items.concat( plotMeasures.getBinNameForTotal() ); // add empty row item for measures requiring no color bin
            }
            analysis.rowTypes.push( oneType );
        }
        if( temp.hasBodyRowsForDivBins ) {
            analysis.rowTypes.push({ items:plotMeasures.divBinNames, selectorName:"divBinIndex", selectorType:"itemIndex" });
        }
        if( temp.hasBodyRowsForHistoBins ) {
            analysis.rowTypes.push({ items:plotMeasures.histoBinNames, selectorName:"histoBinIndex", selectorType:"itemIndex" });
        }
        if( temp.hasBodyRowsForLineID ) {
            // let blankRowsLength = thisSet.counts.lineIndex,
            //     blankRowsArray = Dyn.dataUtil.filledArray( blankRowsLength, "" );
            analysis.rowTypes.push({ items:thisSet.paramSets.lineID, selectorName:"lineID", selectorType:"itemName",
                isSelectable:true, hasLineID:true });
        }
        if( temp.hasBodyRowsForMeasures ) {
            analysis.rowTypes.push({ items:thisSet.measureNames, selectorName:"measureName", selectorType:"itemName" });
        }
        if( temp.hasBodyRowsForModelNumber ) {
            analysis.rowTypes.push({ items:thisSet.paramSets.modelNumber, selectorName:"modelNumber", selectorType:"itemName" });
        }
        if( analysis.rowTypes.length === 0 ) { // add a blank row header if none, may not be visible
            analysis.rowTypes.push({ items:[""], selectorType:"none" }); // no selector
        }

        // build the list of title types (analysis.titleTypes) which determines what is in the title
        if( temp.hasMeasuresInTitle ) {
            analysis.titleTypes.push({ items:thisSet.measureNames, selectorName:"measureName", selectorType:"itemName" });
        }
    }

    /**
     * Analysis step 3 of 3...final stats.
     * @param plotMeasures {Dyn.PlotMeasures}
     * @private
     */
    _analyzeRowsColumns( plotMeasures ) {
        function getItemCombinations( total, rowColType ) {
            // for colTypesArray.Reduce() or rowTypesArray.Reduce() to get number of combinations of all types
            // e.g. 3 attributes * 2 categories * 5 measures = 30 rows in total
            return total * (rowColType.items ? rowColType.items.length : 1);
        }

        // count items in table for verification and debugging.
        let temp = this.temp,
            thisSet = this.measureSet,
            analysis = this;
        analysis.hasMeasuresInTable = temp.numMeasureNames > 0;
        analysis.hasRowHeaders = ((analysis.rowTypes.length > 0) && (analysis.rowTypes[0].selectorType !== "none"));
        analysis.hasColumnHeaders = ((analysis.colTypes.length > 0) && ((analysis.colTypes[0].selectorType !== "none") || temp.haveColumnHeadingForRowType));
        analysis.numRowsInBody = analysis.rowTypes.reduce( getItemCombinations, 1 );
        analysis.numColumnsInBody = analysis.colTypes.reduce( getItemCombinations, 1 );
        analysis.numColumnsForRowHeadings = analysis.hasRowHeaders ? analysis.rowTypes.length : 0;
        analysis.numRowsForColumnHeadings = analysis.hasColumnHeaders ? analysis.colTypes.length : 0;
        analysis.numColumnsTotal = (analysis.numColumnsForRowHeadings + analysis.numColumnsInBody);
        analysis.numRowsTotal = (/*analysis.numRowsForColumnHeadings+*/analysis.numRowsInBody); // Note: rows do not include the rows for the column headings
        //analysis.numTables = Math.max( 1, plotMeasures.sets.length );
        // copy some parameters from the Dyn.MeasureSet that matches this table
        analysis.tableSetID = thisSet.setID;
        analysis.hiddenIDs = thisSet.hiddenIDs;
    }
}


class SummaryViewComponent extends React.Component {

    constructor(props) {
        super(props);
        this.summaryViewRef = React.createRef();
        this.state = {
            precision: this.precisionDisplayString()
        };
    }

    componentDidMount = () => {
        let Dyn = this.props.Dyn;
        this.subscriptions = [];
        Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:measuresUpdated", (args) => {
            this.tableArgs = args;
            this.updateTables();
        });
        Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setCasesSelected", this.handleSelectionMessages);
        Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:adornmentSelected", this.handleSelectionMessages);
        Dyn.mediatorLocalSubscribe( this.subscriptions, "pref:newPrecision", (args) => {
            this.setState({
                precision: this.precisionDisplayString()
            })
        });
    }

    componentWillUnmount = () => {
        this.props.Dyn.mediatorLocalRemoveAll( this.subscriptions );
    }

    /**
     * populate the title string for a table
     * @param plotMeasures -- the source data for measures and axis values in the plot
     * @param analysis -- analysis of what values should go in rows and columns
     * @private
     */
    _constructTitle = ( plotMeasures, analysis ) => {
        let Dyn = this.props.Dyn,
            title="";

        if( ! analysis.hasMeasuresInTable ) {
            // title for empty table
            Dyn.assert( analysis.tableSetID === "std", "Table is missing measures:"+analysis.tableSetID );
            title = plotMeasures.tableTitles.blank;
        } else if( analysis.titleTypes.length > 0 ) {
            // title from the list of items in the title (e.g. measure name "Count"); see hasMeasuresInTitle
            analysis.titleTypes.forEach( function ( oneTitleType ) {
                oneTitleType.items.forEach( function ( itemString ) {
                    title += (title.length > 0 ? ", " : "") + itemString;
                } );
            } );
        } else {
            // custom title for this set of measures
            title = plotMeasures.tableTitles[ analysis.tableSetID ];
        }
        Dyn.assert( typeof title === "string", "Missing title of measure set; update plotMeasures.tableTitles or hasMeasuresInTitle flag" );
        return Dyn.loc( ""+title ); // make sure it is a string, and translate
    }

	/**
     * populate "columns", an array headings for each column (assume one header row for now)
     * @param plotMeasures -- the source data for measures and axis values in the plot
     * @param analysis -- analysis of what values should go in rows and columns
     * @param columns - column table data to construct
     * @private
     */
    _constructColumns = ( plotMeasures, analysis, columns ) => {
        var colIndex = 0;

        function addColumn( valueString, colType ) {
            // add another value to the columns array.
            // columns[index].value will be the table cell value, other parameters are data for highlighting and selection
            var attributeID,
                cell = { value:valueString, col:(colIndex - analysis.numColumnsForRowHeadings) };

            if( colType ) {
                if ( colType.selectorType ) {
                    cell.selector = colType.selectorType;

                    if ( colType.selectorType === "attributeID" ) {
                        cell.attributeID = valueString;
                        cell.value = plotMeasures.getAttributeNameFromID( cell.attributeID );
                    }
                }
                if ( colType.isCategory ) {
                    cell.isSelectable = true; // click on cell to select categories
                    cell.isCategory  = true;
                    cell.attributeID = colType.attributeID;
                }
            }
            columns[colIndex] = cell;
            colIndex++;
        }

        // add a blank columns above each row heading, on left
        if ( analysis.hasRowHeaders ) {
            analysis.rowTypes.forEach( function ( rowHeading ) {
                // Note: if we want to merge cells, we might, in the future, use colSpan metadata here (see rowSpan use elsewhere)
                addColumn( rowHeading.headingName ? rowHeading.headingName : "" );
            } );
        }
        // add columns above body on right
        // NOTE: if analysis.hasColumnHeaders===false, this will be a single blank column that does not show in header
        analysis.colTypes.forEach( function ( colType ) {
            colType.items.forEach( function ( colHeading ) {
                addColumn( colHeading, colType );
            } );
        } );
    }

    _customCellTypes = null;
    _getCustomCellTypes = ( plotMeasures ) => {
        // map of measure name to cell flag, for table cells that need custom HTML/custom handling
        if( ! this._customCellTypes ) {
            // initialize using measure names from plotMeasures object
            this._customCellTypes = {};
            this._customCellTypes[plotMeasures.getNameOfColorMeasure()] = 'isColorCell';
            this._customCellTypes[plotMeasures.getNameOfFormulaMeasure()] = 'isFormulaCell';
        }
        return this._customCellTypes;
    }

    /**
     * Add a single row of cells to the table
     * Each row is an array of cell values (column values), including the row headings (left-hand title cells).
     *  Example rows: [ "length", "mean", "12 ], or ["Male", "Count", 100, 5, 17]
     * @param plotMeasures -- the source data for measures and axis values in the plot
     * @param analysis -- analysis of what values should go in rows and columns
     * @param rowHeadingIndices -- array of indicies corresponding to items in analysis.rowTypes[] array
     * @param tableRows -- array of row data to construct
     * @private
     */
    _constructOneRow = ( plotMeasures, analysis, rowHeadingIndices, tableRows ) => {
        let Dyn = this.props.Dyn;
        var rows = tableRows,
            rowIndex = rows.length,
            customCellTypes = this._getCustomCellTypes( plotMeasures ),
            selector = {};

        // add a cell value to the last row: { value, [rowSpan], [columnIndex], [measureName], etc }
        // NOTE: here we also determine rowSpan for merging multiple cells into one
        function addCellToRow( cellValue, cellMetaData, rowType ) {
            // if necessary create the row so we can add the first cell
            if ( rows.length === rowIndex ) {
                rows[rowIndex] = [];
            }
            let thisRow = rows[rowIndex];

            // set the cell value and metadata (for selection, highlighting, etc)
            var columnIndex = cellMetaData && cellMetaData.columnIndex,
                isRowHeader = columnIndex < analysis.numColumnsForRowHeadings,
                cell = { value: cellValue };
            if( cellMetaData ) {
                if ( cellMetaData.columnIndex !== undefined )
                    cell.columnIndex = cellMetaData.columnIndex;
                if ( cellMetaData.measureName !== undefined ) {
                    cell.measureName = cellMetaData.measureName;
                    if( customCellTypes[cell.measureName]) {
                        cell[ customCellTypes[cell.measureName]] = true; // e.g. cell.isColorCell = true;
                    }
                }
                if( cellMetaData.hiddenLineID ) {
                    thisRow.isSelectable = true; // click on row to select lines in plot
                    thisRow.hasHiddenLineID = true;
                    if( thisRow.length ) {       // 1st cell has selection ID for row
                        thisRow[0].hiddenLineID = cellMetaData.hiddenLineID;
                    }
                }
            }
            if( rowType && rowType.isSelectable ) {
                if( rowType.isCategory ) {
                    cell.isSelectable = true; // click on cell to select categories
                    cell.isCategory = true;
                    cell.attributeID = rowType.attributeID;
                } else if( rowType.hasLineID ) {
                    thisRow.isSelectable = true; // click on row to select lines in plot
                    thisRow.hasLineID = true;
                }
            }


            // if a header, check for cells needing to be multiple rows high (rowSpan > 1)
            if( isRowHeader ) {
                // Consider the cell as header when the columnIndex of the cell is less than analysis.numColumnsForRowHeadings
                cell.header = true;
                var _rowIndex = getLeastRowIndexMatchByCellValue(cellValue, rowIndex, columnIndex);
                if( _rowIndex >= 0 ) {
                    // Increase the rowspan count
                    rows[_rowIndex][columnIndex].rowSpan = (rows[_rowIndex][columnIndex].rowSpan || 1) + 1;
                    // Skip the cell from plotting if the value is mateched with previous row cell value
                    cell.skip = true;
                }
            }

            // Push cell object to row
            rows[rowIndex].push(cell);
        }
        // Get least row index by comparing previous rows cell value based on rowIndex, columnIndex and cellValue
        // used to determine rowSpan for merging multiple cells into one
        function getLeastRowIndexMatchByCellValue(cellValue, rowIndex, columnIndex) {
            // Get previous row index
            rowIndex--;
            if( rowIndex < 0 || rows[rowIndex][columnIndex].value !== cellValue ) {
                return -1;
            } else {
                var _rowIndex = getLeastRowIndexMatchByCellValue(cellValue, rowIndex, columnIndex);
                if( _rowIndex >= 0 ) {
                    return _rowIndex;
                } else {
                    return rowIndex;
                }
            }
        }
        // for measures that may repeat (like Mode), append the extra values to the last cell
        function appendValueToLastCell( additionalCellValue ) {
            var thisRow = rows[rowIndex],
                cellIndex = thisRow.length - 1;
            thisRow[cellIndex].value += ", "+additionalCellValue;
        }
        // add the matching parameter:value for this rowType or colType, used for findMatchingMeasures( selector )
        function addSelector( rowColType, itemIndex ) {
            var itemValue = rowColType.items[ itemIndex],
                itemName = itemValue; // in most cases item name===itemValue

            if( rowColType.selectorType === "attributeID" ) {
                selector.attributeID = itemValue; // // e.g. selector:{ attributeID:"myattributeID" }
                itemName = plotMeasures.getAttributeNameFromID( itemValue ); // display attribute name
            } else if( rowColType.selectorType === "itemName") {
                selector[ rowColType.selectorName ] = itemName; // e.g. selector:{ measureName:Mean }
            } else if( rowColType.selectorType === "itemIndex") {
                selector[ rowColType.selectorName ] = itemIndex; // e.g. selector:{ xBinIndex:5 }
            } else {
                Dyn.assert( rowColType.selectorType === "none" ); // no selector, for a blank cell
            }
            return itemName;
        }

        // add measure in title to selectors used by findMatchingMeasures(): e.g. two-way table of count, add measureName:"Count"
        analysis.titleTypes.forEach( function ( oneTitleType ) {
            addSelector( oneTitleType, 0 /* unused */ );
        });

        // create cells for row headings
        //      (rowHeadingIndices.length === number of columns that are headings, not body)
        //      example: "Male", "Count" as the two headings in this row
        if( analysis.hasRowHeaders ) {
            rowHeadingIndices.forEach( function ( rowHeadingIndex, columnIndex ) {
                var rowType  = analysis.rowTypes[columnIndex],
                    cellName = addSelector( rowType, rowHeadingIndex ),
                    cellMetaData = { columnIndex: columnIndex };
                addCellToRow( cellName, cellMetaData, rowType );
            } );
        }

        // create cells for body
        //      Example: "2", "3.5", if there are two column headings in body of table.
        analysis.colTypes.forEach( function( colType ) {
            colType.items.forEach( function( colItemValue, colItemIndex ) {
                // add to selector according to column type
                addSelector( colType, colItemIndex );
                // find associated measures, add to cell, or add blank cell
                var measuresArray = plotMeasures.findMatchingMeasures( selector );
                // In special case when we have boxplot and Stats - Median enabled, median value gets returned
                // twice, as boxplot measure also returns Median. below code will avoid duplication of median
                // value - TODO: Craig to review this logic when back from retreat.
                var medianAppended = false; // Flag to track if Median has been appended
                measuresArray.forEach( function( measureData, measureIndex ) {
                    if (measureData.measureName === "Median" && !medianAppended) {
                        let cellMetaData = { measureName: measureData.measureName };
                        analysis.hiddenIDs.forEach(paramName => {
                            if (measureData.hasOwnProperty(paramName)) {
                                cellMetaData[paramName] = measureData[paramName];
                            }
                        });
                        addCellToRow(measureData.value, cellMetaData);
                        medianAppended = true; // Set flag to true once Median is appended
                    } else if (measureData.measureName !== "Median") {
                        if (measureIndex === 0) {
                            let cellMetaData = { measureName: measureData.measureName };
                            analysis.hiddenIDs.forEach(paramName => {
                                if (measureData.hasOwnProperty(paramName)) {
                                    cellMetaData[paramName] = measureData[paramName];
                                }
                            });
                            addCellToRow(measureData.value, cellMetaData);
                        } else {
                            appendValueToLastCell(measureData.value);
                        }
                    }
                });
                if( !medianAppended && measuresArray.length === 0 ) {
                    addCellToRow( plotMeasures.getMissingValueForMeasure( selector )); // missing cell value, use 0 or blank as appropriate.
                }
            });
        });
    }

    /**
	 * construct the table.rows and table.columns data based on Dyn.PlotMeasures
     * @param plotMeasures {Dyn.PlotMeasures}
     * @param table {SingleTable}
     * @param tableIndex {number} -- index of table, starting with table 0
     * @returns {boolean} -- true if more tables are needed
     */
    _constructOneTable = ( plotMeasures, table, tableIndex ) => {
        "use strict";
        let Dyn = this.props.Dyn,
            thisSummaryTableView = this,
            columns = table.columns,
            rows = table.rows,
            thisSet  = (tableIndex < plotMeasures.sets.length ) ? plotMeasures.sets[tableIndex] : new Dyn.MeasureSet(),
            // determine which measures go in this table in which order (e.g. match rows headings, column headings, etc)
            analysis = new MeasureSetAnalysis( plotMeasures, tableIndex, thisSet );

        function constructAllRows( plotMeasures, analysis, rowHeadingIndices ) {
            // recursively iterate through all row types to build a row for each combination of row headings
            //      e.g. 3 row types make the first 3 columns the headings, columns 4+ are the table body.
            //
            var rowTypeIndex = rowHeadingIndices.length; // index of next rowType/column, if any

            if( rowTypeIndex < analysis.rowTypes.length ){
                // iterate through all values of this row type (e.g. all Y Attribute Names in 0th column)
                var rowType = analysis.rowTypes[ rowTypeIndex ];
                rowType.items.forEach( function( rowHeading, rowHeadingIndex ) {
                    // add index of the next item in this
                    rowHeadingIndices[ rowTypeIndex ] = rowHeadingIndex; // e.g. [ 0, 2 ], for 0th Attribute Name, 2nd Measure Name
                    rowHeadingIndices.length = rowTypeIndex + 1; // remove values to right of this row type
                    // recurse to iterate through
                    constructAllRows( plotMeasures, analysis, rowHeadingIndices );
                });
            } else {
                // done adding row types, now construct a single row
                thisSummaryTableView._constructOneRow( plotMeasures, analysis, rowHeadingIndices, rows );
            }
        }

        table.title = this._constructTitle( plotMeasures, analysis );
        table.tableSetID = analysis.tableSetID;
        this._constructColumns( plotMeasures, analysis, table.columns );   // construct column headings (with blank columns above row headings)
        constructAllRows( plotMeasures, analysis, [] );     // construct all rows (with row headings at the start of each row)

        table.showColumnHeaders = analysis.hasColumnHeaders;
        table.showRowHeaders = analysis.hasRowHeaders;
        table.hasContent = ((columns.length > 1) || ( columns.length === 1 && columns[0].value !== "")) &&
                           ((rows.length > 1) || ( rows.length === 1 && rows[0].value !== ""));

        Dyn.assert( columns.length === analysis.numColumnsTotal, "unexpected number of columns in summary table: "+columns.length+" instead of "+analysis.numColumnsTotal );
        Dyn.assert( rows.length === analysis.numRowsTotal, "unexpected number of rows in summary table: "+rows.length+" instead of "+analysis.numRowsTotal );
    }

    _debugCanDisplayLatexUsingMathQuil = true;

    /**
     * Create a new HTML table based on column and row data.
     * HTML table has HTML rows, which have HTML cells.
     */
    _constructHtmlTable = ( table, tableIndex ) => {
        "use strict";
        let Dyn = this.props.Dyn,
            htmlTable     = document.createElement( 'table' ),
            headerRow = htmlTable.insertRow( 0 ),
            dataSet = Dyn.dataSet,
            plotView = Dyn.plotView,
            self = this;
        if( table.showColumnHeaders ) {
            table.columns.forEach( function ( colValue, colIndex ) {
                let htmlCell       = headerRow.insertCell( colIndex );
                htmlCell.innerHTML = '<strong>' + colValue.value + '</strong>';
                // Setting hover style only for selectable cells
                if( colValue.isSelectable ) {
                    self._appendClassName(htmlCell, self._cellHoverClass);
                }

                // Registering click and mouseover events
                htmlCell.onclick = function(){self._onTableCellClick(tableIndex, this, true);};
                htmlCell.addEventListener("mouseover", function(){self._onTableCellHover(tableIndex, this, true);});
                // Set custom attribute index
                htmlCell.setAttribute("headerIndex", colIndex);
            } );
        }

        table.rows.forEach( function ( tableRow, rowIndex ) {
            let htmlRow = htmlTable.insertRow( rowIndex + 1 ),
                htmlCellIndex = 0;
            htmlRow.setAttribute("rowIndex", rowIndex);
            // note: no need for _onTableRowHover() callback, _cellHoverClass is enough
            if( tableRow.isSelectable ) { // entire row is selectable
                self._appendClassName(htmlRow, self._cellHoverClass);
                htmlRow.onclick = function() { self._onTableRowClick( tableIndex, rowIndex, this ); };
            }
            if( self._isTableRowSelectedInPlot( table.tableSetID, tableRow, plotView )) {
                self._appendClassName(htmlRow, self._cellSelectedClass);
            } else {
                self._removeClassName(htmlRow, self._cellSelectedClass);
            }
            tableRow.forEach( function ( rowValue, cellIndex ) {
                if ( !rowValue.skip ) {
                    let htmlCell       = htmlRow.insertCell( htmlCellIndex++ ),
                        isSelected     = false;
                    htmlCell.innerHTML = rowValue.header ? '<strong>' + rowValue.value + '</strong>' : rowValue.value;
                    // Setting hover style only for selectable cell
                    if( rowValue.isSelectable ) { // cell or row is selectable
                        self._appendClassName(htmlCell, self._cellHoverClass);
                        if( rowValue.isCategory ) {
                            isSelected = dataSet.isCategorySelected( rowValue.value, rowValue.attributeID );
                        }
                    }
                    if ( rowValue.rowSpan ) {
                        htmlCell.rowSpan = rowValue.rowSpan;
                    }
                    if( rowValue.isColorCell ){
                        // use color value to make a blank cell with background color
                        htmlCell.bgColor = rowValue.value;
                        htmlCell.innerHTML = ' ';
                    }
                    if( rowValue.isFormulaCell && self._debugCanDisplayLatexUsingMathQuil ) {
                        // TODO: show the Latex formula correctly using MathQuil, just like ModelFunctions.jsx line 533
                        // after debugging remove _debugCanDisplayLatexUsingMathQuil
                        // <div className={`tde-model-formula-item `+ (obj.formula === this.state.formulaString ? "tde-selected" : "")}> <MathQuilElement value={obj.formula}/> </div>
                        // htmlCell.innerHTML = '<div className="tde-model-formula-item"><MathQuilElement value="' + rowValue.value + '"/></div>';
                        const root = ReactDOM.createRoot(htmlCell);
                        root.render(React.createElement(MathQuilElement, {value: rowValue.value}, null));
                        //self._appendClassName(htmlCell.firstChild,'tde-model-formula-item');
                    }
                    if(isSelected) {
                        self._appendClassName(htmlCell, self._cellSelectedClass);
                    } else {
                        self._removeClassName(htmlCell, self._cellSelectedClass);
                    }
                    // Registering click and mouseover events
                    htmlCell.onclick = function(){self._onTableCellClick(tableIndex, this, false);};
                    htmlCell.addEventListener("mouseover", function(){self._onTableCellHover(tableIndex, this, false);});
                    // Set custom attributes row and col
                    htmlCell.setAttribute("rowIndex", rowIndex);
                    htmlCell.setAttribute("cellIndex", cellIndex);
                }
            } );
        } );

        return htmlTable;
    }

    /**
     * Append a className to given element
     */
    _appendClassName = (ele, className) => {
        var classes = ele.className.split(" ");
        if(classes.indexOf(className) === -1 ) {
            classes.push(className);
        }
        ele.className = classes.join(" ");
    }

    /**
     * Remove a className from given element
     */
    _removeClassName = (ele, className) => {
        var classes = ele.className.split(" "),
            index = classes.indexOf(className);
        if(index >= 0) {
            classes.splice(classes.indexOf(className), 1);
            ele.className = classes.join(" ");
        }
    }

    // used for html tr, th, td elements
    _cellSelectedClass = "selected";
    _cellHoverClass = "hover-enabled";

    /**
     * Call back function for summary table cell hover
     */
    _onTableCellHover = (tableIndex, element, isColumnHeader) => {
        var table = this.tables[tableIndex],
            cell;
        if( isColumnHeader) {
            let headerIndex = element.getAttribute('headerIndex');
            cell = table.columns[headerIndex];
            // console.log("Cell Hover", "tableSetID:", table.tableSetID, "column header index:", headerIndex, "Object::", cell );
        } else {
            let rowIndex = element.getAttribute('rowIndex'),
                cellIndex = element.getAttribute('cellIndex');
            cell = table.rows[rowIndex][cellIndex];
            // console.log("Cell Hover", "tableSetID:", table.tableSetID, "row,col: "+rowIndex+","+cellIndex+" Object::", cell );
        }
    }

    /**
     * Call back function for summary table cell click
     */
    _onTableCellClick = (tableIndex, element, isColumnHeader) => {
        const viewOnly = this.props.Dyn.preferences.isViewOnly();
        if(viewOnly) {
            return;
        }
        let mediator = this.props.Dyn.mediator;
        var table = this.tables[tableIndex],
            cell;
        if( isColumnHeader) {
            let headerIndex = element.getAttribute('headerIndex');
            cell = table.columns[headerIndex];
            console.log("Hover:: Header index:"+headerIndex+" Object::", cell );
        } else {
            let rowIndex = element.getAttribute('rowIndex'),
                cellIndex = element.getAttribute('cellIndex');
            cell = table.rows[rowIndex][cellIndex];
            console.log("Cell Click", "tableSetID", table.tableSetID, "Object::", cell );
        }
        if( cell && cell.isSelectable ) {
            if( cell.isCategory ){
                // select the cases with the given category for this attribute
                // Note: case table deselects plot objects when selecting cases
                let data = { "attributeID":cell.attributeID, "category":cell.value /*, addToSelection:wantAdd, isNumericInterval:isNumericInterval*/ };
                mediator.publish( "records:casesSelectedByCategory", data );
            }
            // and/or select any associated plot objects by value; plot must interpret what this is
            // NOTE: for future use; we probably just want to copy the tableCell parameters along with the tableSetID
            mediator.publish( "summary:selectCell",
                { tableSetID:table.tableSetID, selectedValue:cell.value, isCategory:cell.isCategory } );
        } else {
            // reset category selection and case selection, don't toggle case card
            var deselectAllData = { ids: [], object: "plot", disableToggle: true };
            mediator.publish( "records:casesSelected", deselectAllData );
        }
    }

    /**
     * Call back function for summary table row click
     */
    _onTableRowClick = (tableIndex, rowIndex, element /*, isColumnHeader */ ) => {
        let Dyn = this.props.Dyn;
        const viewOnly = Dyn.preferences.isViewOnly();
        if(viewOnly) {
            return;
        }
        let mediator = this.props.Dyn.mediator,
            table = this.tables[tableIndex],
            tableRow = table.rows[rowIndex],
            rowIndexViaAttr = element.getAttribute('rowIndex'),
            data = { tableSetID:table.tableSetID };
        Dyn.assert( rowIndex === Number( rowIndexViaAttr ), "inconsistent row index" );
        Dyn.assert( Dyn.PlotMeasures.prototype.knownSetIDs.includes( table.tableSetID ), "unknown set id");
        if( tableRow && tableRow.isSelectable && tableRow.length>0 ) {
            let cell0 = tableRow[0];
            if( tableRow.hasLineID ) {
                data.lineID = cell0.value; // needed to ID movable lines, reference lines
            } else if( tableRow.hasHiddenLineID ) {
                data.hiddenLineID = cell0.hiddenLineID; // needed to ID least squares lines
            }
            //console.log( "Row Click", "tableSetID", table.tableSetID, tableRow );
            // notify plot that it should select matching plot adornment
            mediator.publish( "summary:selectRow", data );
        }
    }

	/**
	 * Create or update 1+ summary view tables based plot measures;
     * each table has data to construct its own htmlTable.
     * uses this.tableArgs -- plot measures object (and possibly other parameters in the future)
     */
    updateTables = () => {
        let plotMeasures = this.tableArgs;
        /**
         * constructor for a single table, which has the data necessary to create an html table
         * @constructor
         */
        function SingleTable() {
            this.showColumnHeaders = true;
            this.columns = [];     // the column cell values of the first row (a header row)
            this.rows = [];
            this.title = "";
            this.htmlTable = null;
            this.htmlTitle = "";
        }

        var table, needAnotherTable,
            gridContainer = this.refs.gridContainer,
            numTables = Math.max( 1, plotMeasures.sets.length );

        // (re-)build 1+ SingleTables from 0+ measure sets
        this.tables = [];
        do {
            table = new SingleTable();
            this._constructOneTable( plotMeasures, table, this.tables.length );
            this.tables.push( table );
        } while ( this.tables.length < numTables );
        // until we have 1+ tables and all measure sets displayed

        // (re-)build HTML table from SingleTable and analysis of measures
        this.tables.forEach( ( table, tableIndex ) => {

            // construct HTML for title and table
            if ( table.title ) {
                table.htmlTitle           = document.createElement( 'h3' );
                table.htmlTitle.classList.add("tde-summary-inner-title"); // font size from _variables.scss
                table.htmlTitle.innerHTML = table.title;
            }
            table.htmlTable = this._constructHtmlTable( table, tableIndex );

            // insert HTML into document
            if( tableIndex===0 ) {
                while (gridContainer.firstChild) {
                    gridContainer.removeChild(gridContainer.firstChild);
                }
                gridContainer.appendChild(table.htmlTitle);
            } else {
                gridContainer.appendChild( table.htmlTitle );
            }
            if( table.hasContent ) {
                gridContainer.appendChild( table.htmlTable );
            }
        });

        // Render mathjax after summary table update
        if (window.hasOwnProperty('MathJax')) {
            MathJax.Hub.Queue(['Typeset', MathJax.Hub, this.summaryViewRef.current]);
        }
    }

    /**
     * Handle a "records:setCasesSelected" message from Dyn.DataSet,
     * or a "plot:adornmentSelected" message from Dyn.PlotView
     * @param args
     */
    handleSelectionMessages = ( args ) => {
        // update the table style to match selection
        let Dyn = this.props.Dyn,
            dataSet = Dyn.dataSet,
            plotView = Dyn.plotView,
            self = this;

        function updateCellSelection( htmlCell, tableCell /*, args */ ) {
            // add/remove selection class to one html cell (th, td)
            var isSelected = false;
            if( tableCell.isSelectable ){
                if( tableCell.isCategory ){
                    isSelected = dataSet.isCategorySelected( tableCell.value, tableCell.attributeID );
                } else if( tableCell.hasLineID ){
                    //isSelected = Dyn.plotView.plotShaper.isAdornmentSelected( Dyn.EPlotControl.kControlsDiagRefLine, tableCell.value );
                }
            }
            if(isSelected) {
                self._appendClassName(htmlCell, self._cellSelectedClass);
            } else {
                self._removeClassName(htmlCell, self._cellSelectedClass);
            }
        }
        function updateRowSelection( htmlRow, tableRow, tableSetID /*, args */ ) {
            // add/remove selection class to one html row (tr)
            if( self._isTableRowSelectedInPlot( tableSetID, tableRow, plotView ) ) {
                self._appendClassName( htmlRow, self._cellSelectedClass);
            } else {
                self._removeClassName( htmlRow, self._cellSelectedClass);
            }
        }

        this._forEachTableCell( updateCellSelection, updateRowSelection, args );
    }

    _isTableRowSelectedInPlot = ( tableSetID, tableRow, plotView ) => {
        let Dyn = this.props.Dyn,
            isSelected = false;
        if( plotView?.plotShaper && tableRow.isSelectable && tableRow.length ){
            let cell0 = tableRow[0];
            if( tableRow.hasLineID ){
                isSelected = plotView.plotShaper.isAdornmentSelected( tableSetID, cell0.value );
            } else if ( tableRow.hasHiddenLineID ) {
                isSelected = plotView.plotShaper.isAdornmentSelected( tableSetID, cell0.hiddenLineID );
            }
        }
        return isSelected;
    }

    /**
     * Call cellFunction( htmlCell, tableCell, args ) on each htmlCell in all tables constructed by _constructHtmlTable(),
     * and rowFunction( htmlRow, tableRow, args ) on each non-header htmlRow in all tables as well.
     * @param cellFunction
     * @param args
     * @private
     */
    _forEachTableCell = ( cellFunction, rowFunction, args ) => {
        this.tables?.forEach( function( table /*, tableIndex */ ) {
            for( let r=0, rr=table.htmlTable.rows.length; r<rr; ++r ) {
                let htmlRow = table.htmlTable.rows[r],
                    isColumnHeader = table.showColumnHeaders && r === 0;
                for( let c=0, cc=htmlRow.cells.length; c<cc; ++c ) {
                    let  htmlCell = htmlRow.cells[c],
                         tableCell;
                    if( isColumnHeader) {
                        let headerIndex = htmlCell.getAttribute('headerIndex');
                        tableCell = table.columns[headerIndex];
                    } else {
                        let rowIndex = htmlCell.getAttribute('rowIndex'),
                            cellIndex = htmlCell.getAttribute('cellIndex'),
                            tableRow = table.rows[rowIndex];
                        tableCell = tableRow[cellIndex];
                        if( cellIndex=="0" ) {
                            // if 1st cell of non-header row, we have an associated tableRow
                            rowFunction( htmlRow, tableRow, table.tableSetID, args );
                        }
                    }
                    cellFunction( htmlCell, tableCell, args );
                }
            }
        });
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        // Return true when language value is changed
        return nextProps.lang !== this.props.lang || nextState.printClassName != this.state.printClassName || nextProps.precision !== this.state.precision;
    }

    setFocus = () => {
        this.summaryViewRef.current.focus();
    }

    getLoc = (str) => {
        let Dyn = this.props.Dyn;
        // NOTE: translation may be missing on first render, before Dyn.dataset constructed, later rerendering will fix this.
        return ( Dyn.dataSet ) ? Dyn.loc(str) : str;
    }

    // Render the UI
    render = () => {
        if(this.tableArgs) {
            setTimeout(() => {
                this.updateTables();
            });
        }

        return (
            <div ref={this.summaryViewRef} tabIndex="0" className="tde-summary-table">
                <div className="tde-summary-table-buttons tde-bg-color">
                    {this.getPrecisionControl()}
                    <div onClick={this.printMe} className="tde-print-icon"><i className="tde-i tde-i-print" title="Print"></i></div>
                </div>
                <div ref="gridContainer" className={"tde-summary-container " + (this.state.printClassName || '') }></div>
            </div>
        )
    }

    getPrecisionControl() {
        const viewOnly = this.props.Dyn.preferences.isViewOnly();
        if(!viewOnly) {
            return <Fragment>
                <div onClick={this.lessPrecision} className="tde-precision-minus-icon"><i className="tde-i tde-i-minus" title={this.getLoc("Less precision (more rounding)")}></i></div>
                <div onClick={this.morePrecision} className="tde-precision-plus-icon"><i className="tde-i tde-i-add" title={this.getLoc("More precision (less rounding)")}></i></div>
                <div min-width="100px" className="tde-precision-value" title={this.getLoc("extra digits added to statistic's significant figures")}>{ this.state.precision }</div>
            </Fragment>
        }
    }

    printMe = () => {
        this.refs.gridContainer?.scrollTo(0,0);
        this.setState({
            printClassName: 'tde-print-me'
        });
        setTimeout(() => {
            window.print();
            this.setState({
                printClassName: ''
            });
        }, 200);
    }

    morePrecision = () => {
        let preferences = this.props.Dyn.preferences;
        preferences.setPrecision( preferences.getPrecision() + 1 );
    }

    lessPrecision = () => {
        let preferences = this.props.Dyn.preferences;
        preferences.setPrecision( preferences.getPrecision() - 1 );
    }

    precisionDisplayString = () => {
        let p = this.props.Dyn.preferences.getPrecision();
        return p==0? "" : ("["+((p<0)?"-":"+")+Math.abs(p)+"]");
    }

}

export default SummaryViewComponent
